// react
import { List, fromJS, isImmutable, toJS } from "immutable";
import moment from "moment";
import React from "react";
import { connect } from "react-redux";

import { InputWrapper } from "@performyard/py-components";

// components
import Button from "../../../../../components/Button";
import { SingleSelectForm } from "../../../../../components/Inputs/SelectForm";
import ReviewFormCard from "../../../../../components/ReviewFormCard";

import {
	addFormToCycle,
	appendNewForm,
	removeForm,
	replaceFormInCycle,
	updateEditingForms,
	updateTemplateAssignment,
} from "../../actions";

// selectors
import { getCompanyLimit } from "../../../../../selectors/settings";
import { getEditingForms, getReviewTemplateForms, getStartDateTime } from "./selectors";

import PropTypes from "../../../../../lib/prop-types";

// styles
import css from "./createForm.module.scss";

class CreateReviewCycleForm extends React.Component {
	static propTypes = {
		cycleID: PropTypes.string,
		appendNewForm: PropTypes.func.isRequired,
		removeForm: PropTypes.func.isRequired,
		updateTemplateAssignment: PropTypes.func.isRequired,
		addFormToCycle: PropTypes.func.isRequired,
		replaceFormInCycle: PropTypes.func.isRequired,
		updateEditingForms: PropTypes.func.isRequired,
		canUserChangeRedactedness: PropTypes.bool.isRequired,
		config: PropTypes.object.isRequired,
		maxFormsCount: PropTypes.number.isRequired,
		startDateTime: PropTypes.instanceOf(moment),
		templateForms: PropTypes.instanceOf(List).isRequired,
		editingForms: PropTypes.instanceOf(List).isRequired,
		replace: PropTypes.bool,
		cancelReplace: PropTypes.func.isRequired,
		formToReplace: PropTypes.object,
		formType: PropTypes.oneOf(["basic", "external"]).isRequired,
	};

	static defaultProps = {
		startDateTime: null,
		cycleID: null,
		replace: false,
		formToReplace: null,
	};

	state = {
		reviewFormInput: "",
		expandNewForm: false,
		prevPropsReplace: this.props.replace,
	};

	// if the active tab has changed (between basic and external forms),
	// if the select form input was expanded prior to the tab change,
	// collapse the input and show the add form button
	componentDidUpdate(prevProps) {
		if (prevProps.formType !== this.props.formType) {
			if (this.state.expandNewForm === true) {
				this.toggleExpandNewForm();
			}
		}
	}

	static getDerivedStateFromProps(props, state) {
		// Any time the current user changes,
		// Reset any parts of state that are tied to that user.
		// In this simple example, that's just the email.
		if (props.replace !== state.prevPropsReplace && props.replace === true) {
			// This covers case where replace is changing from false to true.
			// i.e. the replace option has been selected.
			// we want replace to be true, expand new form true
			// (so user can select form)
			// and addForm button invisible.
			return {
				prevPropsReplace: props.replace,
				expandNewForm: true,
			};
		}
		return null;
	}

	toggleExpandNewForm = () => {
		this.setState({ expandNewForm: !this.state.expandNewForm });
	};

	toggleNewFormContainer = () => {
		this.toggleExpandNewForm();

		this.props.cancelReplace();

		if (this.props.replace === true) {
			// cancelReplace will change replace to false,
			// we want prevPropsReplace to reflect this so that
			// clicking replace will open the form selection dropdown.
			this.setState({ prevPropsReplace: false });
		}
	};

	toggleEditForm = (formID) => {
		const { editingForms } = this.props;
		const isEditing = editingForms.includes(formID);

		if (isEditing) {
			this.props.updateEditingForms(editingForms.filter((id) => id !== formID));
		} else {
			this.props.updateEditingForms(editingForms.push(formID));
		}
	};

	handleSave = (formID, state, cycleID) => {
		this.props.updateTemplateAssignment(formID, state);
		this.props.addFormToCycle(formID, cycleID, this.props.formType);
	};

	handleDeleteForm = (formID) => {
		this.props.removeForm(formID);
	};

	handleFormChange = (value) => {
		// if a form has been selected
		if (isImmutable(value)) {
			// if !replace then we are adding a form.
			if (!this.props.replace) {
				this.setState({ expandNewForm: false, reviewFormInput: "" }, () => {
					this.props.appendNewForm(value, this.props.formType);
				});
			} else {
				const replacement = {
					id: value.get("id"),
					name: value.get("name"),
				};
				this.props.replaceFormInCycle(this.props.formToReplace, replacement);
				// After replace is complete, act as if replace was cancelled.
				// This allows clicking Replace button to be triggered again.
				this.props.cancelReplace();
				if (this.props.replace) {
					// cancelReplace will change replace to false,
					// we want prevPropsReplace to reflect this so that
					// clicking replace will open the form selection dropdown.
					this.setState({ prevPropsReplace: false });
				}
			}
		}
		// if user is searching for a form.
		else {
			this.setState({ reviewFormInput: value });
		}
	};

	render() {
		const { expandNewForm, reviewFormInput } = this.state;
		const {
			cycleID,
			editingForms,
			templateForms,
			maxFormsCount,
			startDateTime,
			canUserChangeRedactedness,
			config: Config,
			replace,
			formType,
		} = this.props;
		const { formWidth } = Config;

		const horizontalMargin = 0;
		const filteredForms = templateForms.filter((form) => form.get("type") === formType);

		/** if other fields need to be locked, they can be added it this object.
		 * Currently, only redactedness could be locked (for a group admin) */
		const lockedFields = canUserChangeRedactedness ? [] : ["redactedness"];

		return (
			<section
				className="react-page create-review-cycle-page form-border"
				style={{ margin: "0 auto", width: formWidth }}
			>
				<section className="page-inner-container" style={{ margin: `0 ${horizontalMargin}px` }}>
					<div className={css.templateForms}>
						{filteredForms.map((form) => {
							const formID = form.get("id");
							return (
								<ReviewFormCard
									className={css.templateForm}
									key={formID}
									type={form.get("type")}
									form={form.get("form")}
									assignment={form.get("assignment")}
									lockedFields={List(lockedFields)}
									onEdit={() => this.toggleEditForm(formID)}
									onCancel={
										form.get("unsaved", false)
											? () => this.handleDeleteForm(formID)
											: () => this.toggleEditForm(formID)
									}
									isEditing={editingForms.includes(formID)}
									onDelete={() => this.handleDeleteForm(formID)}
									onSave={(nextState) => this.handleSave(formID, nextState, cycleID)}
									minimumDueDate={startDateTime}
									invalidMinimumDueDateMessage="The due date and signer date can not be before the review start date."
									isNewForm={form.get("isNewForm")}
								/>
							);
						})}

						{filteredForms.size >= maxFormsCount ? null : (
							<div className={`${css.addFormContainer} ${expandNewForm ? css.isExpanded : ""}`}>
								{expandNewForm ? (
									<div className={css.reviewFormContainer}>
										<InputWrapper
											id="addForm"
											label={replace ? "Select replacement form" : "Select review form"}
										>
											<SingleSelectForm
												value={
													reviewFormInput
														? toJS({
																name: reviewFormInput?.name,
																_id: reviewFormInput?.id,
															})
														: null
												}
												onChange={(value) => {
													value = fromJS({ name: value?.name, id: value?._id });
													this.handleFormChange(value);
												}}
											/>
										</InputWrapper>
										<div className={css.controls}>
											<Button
												className={css.cancel}
												color="grey"
												onClick={this.toggleNewFormContainer}
											>
												Cancel
											</Button>
										</div>
									</div>
								) : null}
								{filteredForms.size > 0 || expandNewForm ? null : (
									<div
										data-testid="add-form"
										className={css.addFormButton}
										onClick={this.toggleNewFormContainer}
									>
										{formType === "basic" ? "Add New Form" : "Add New External Form"}
									</div>
								)}
							</div>
						)}
					</div>
				</section>
			</section>
		);
	}
}

const mapStateToProps = (state) => {
	const cycleID = state.navigation.getIn(["page", "values", "cycleId"]);
	return {
		cycleID,
		editingForms: getEditingForms(state),
		startDateTime: getStartDateTime(state, null),
		templateForms: getReviewTemplateForms(state),
		maxFormsCount: getCompanyLimit(state, "MAXFORMSPERREVIEWTEMPLATE", 100),
	};
};

const mapDispatchToProps = (dispatch) => ({
	removeForm: (formID) => dispatch(removeForm(formID)),
	appendNewForm: (form, formType) => dispatch(appendNewForm(form, formType)),
	updateEditingForms: (formsList) => dispatch(updateEditingForms(formsList)),
	addFormToCycle: (formID, cycleID, formType) => dispatch(addFormToCycle(formID, cycleID, formType)),
	replaceFormInCycle: (replace, replacement) => dispatch(replaceFormInCycle(replace, replacement)),
	updateTemplateAssignment: (formID, nextState) => dispatch(updateTemplateAssignment(formID, nextState)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CreateReviewCycleForm);
